import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Checkout = () => {
  const navigate = useNavigate();
  const [selectedPayment, setSelectedPayment] = useState("");
  const [email, setEmail] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [shipping, setShipping] = useState(0);
  const [zone, setZone] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    cardNumber: "",
    expiry: "",
    cvv: "",
    cardName: "",
  });

  useEffect(() => {
    const userEmail = sessionStorage.getItem("userEmail");
    const cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    setEmail(userEmail || "");
    setCartItems(cart);

    const zones = [
      { name: "East Coast", price: 8.99 },
      { name: "West Coast", price: 12.49 },
      { name: "Midwest", price: 9.79 },
      { name: "South", price: 14.89 },
      { name: "Central", price: 6.75 },
      { name: "Mountain", price: 10.25 },
    ];
    const randomZone = zones[Math.floor(Math.random() * zones.length)];
    setZone(randomZone.name);
    setShipping(randomZone.price);
  }, []);

  const subtotal = cartItems.reduce(
    (acc, item) => acc + parseFloat(item.price || 0) * (item.quantity || 1),
    0
  );
  const tax = subtotal > 0 ? subtotal * 0.1 : 0;
  const total = subtotal + tax + shipping;

  const handlePaymentChange = (event) => {
    setSelectedPayment(event.target.value);
  };

  // ✅ Update form data
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ✅ Validation before placing order
  const handlePlaceOrder = () => {
    const {
      firstName,
      lastName,
      phone,
      address,
      city,
      state,
      zip,
      cardNumber,
      expiry,
      cvv,
      cardName,
    } = formData;

    // General fields check
    if (
      !firstName ||
      !lastName ||
      !phone ||
      !address ||
      !city ||
      !state ||
      !zip
    ) {
      toast.error("Please fill in all required fields!");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("Your cart is empty!");
      return;
    }

    if (!selectedPayment) {
      toast.error("Please select a payment method!");
      return;
    }

    // Credit card-specific validation
    if (selectedPayment === "creditCard") {
      if (!cardNumber || !expiry || !cvv || !cardName) {
        toast.error("Please fill in all credit card details!");
        return;
      }

      const cleanCard = cardNumber.replace(/\s+/g, "");
      if (cleanCard === "4242424242424242") {
        toast.error("Test card number 4242 4242 4242 4242 is invalid!");
        return;
      }

      if (cvv === "123") {
        toast.error("CVV 123 is invalid!");
        return;
      }

      const cardPattern = /^[0-9]{16}$/;
      const cvvPattern = /^[0-9]{3,4}$/;
      if (!cardPattern.test(cleanCard) || !cvvPattern.test(cvv)) {
        toast.error("Invalid credit card details!");
        return;
      }
    }

    sessionStorage.setItem("firstName", formData.firstName);
    sessionStorage.setItem("lastName", formData.lastName);
    sessionStorage.setItem("phone", formData.phone);
    sessionStorage.setItem("address", formData.address);
    sessionStorage.setItem("lastOrder", JSON.stringify(cartItems));
    sessionStorage.setItem("orderSubtotal", subtotal.toFixed(2));
    sessionStorage.setItem("orderTax", tax.toFixed(2));
    sessionStorage.setItem("orderShipping", shipping.toFixed(2));
    sessionStorage.setItem("orderTotal", total.toFixed(2));

    // ✅ Everything valid — now store order data
    sessionStorage.setItem("lastOrder", JSON.stringify(cartItems));

    // ✅ Order success
    toast.success("Order placed successfully!");
    sessionStorage.removeItem("cart");

    setTimeout(() => {
      navigate("/order-confirmed");
    }, 1500);
  };

  return (
    <section className="checkout-section py-80">
      <div className="container container-lg">
        <div className="row g-4">
          {/* LEFT SIDE */}
          <div className="col-lg-8">
            {/* Contact Information */}
            <div className="checkout-card mb-4">
              <div
                className="checkout-card-header text-white rounded-top-8 d-flex align-items-center gap-2"
                style={{
                  background: "#3446b8",
                  padding: "25px",
                }}
              >
                <i className="ph ph-user text-xl"></i>
                <h5 className="mb-0 fw-semibold">Contact Information</h5>
              </div>
              <div className="checkout-card-body bg-white shadow-sm p-4 rounded-bottom-8">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="checkout-label fw-medium mb-5">
                      First Name *
                    </label>
                    <input
                      name="firstName"
                      type="text"
                      className="checkout-input"
                      placeholder="John"
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="checkout-label fw-medium mb-5">
                      Last Name *
                    </label>
                    <input
                      name="lastName"
                      type="text"
                      className="checkout-input"
                      placeholder="Kapoor"
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-12">
                    <label className="checkout-label fw-medium mb-5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      className="checkout-input"
                      value={email}
                      disabled
                    />
                    <small className="text-muted d-block mt-1">
                      Using email from your account
                    </small>
                  </div>
                  <div className="col-md-12">
                    <label className="checkout-label fw-medium mb-5">
                      Phone Number *
                    </label>
                    <input
                      name="phone"
                      type="text"
                      className="checkout-input"
                      placeholder="833-551-6033"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="checkout-card mt-5">
              <div
                className="checkout-card-header text-white rounded-top-8 d-flex align-items-center gap-2"
                style={{
                  background: "#3446b8",
                  padding: "25px",
                }}
              >
                <i className="ph ph-map-pin text-xl"></i>
                <h5 className="mb-0 fw-semibold">Shipping Address</h5>
              </div>
              <div className="checkout-card-body bg-white shadow-sm p-4 rounded-bottom-8">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="checkout-label fw-medium mb-5">
                      Street Address *
                    </label>
                    <input
                      name="address"
                      type="text"
                      className="checkout-input"
                      placeholder="123 Main Street"
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="checkout-label fw-medium mb-5">
                      City *
                    </label>
                    <select
                      name="city"
                      className="checkout-input"
                      onChange={handleChange}
                    >
                      <option value="">Select City</option>
                      <option>New York</option>
                      <option>Los Angeles</option>
                      <option>Chicago</option>
                      <option>Dallas</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="checkout-label fw-medium mb-5">
                      State *
                    </label>
                    <select
                      name="state"
                      className="checkout-input"
                      onChange={handleChange}
                    >
                      <option value="">Select State</option>
                      <option>California</option>
                      <option>Texas</option>
                      <option>Florida</option>
                      <option>Washington</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="checkout-label fw-medium mb-5">
                      ZIP Code *
                    </label>
                    <input
                      name="zip"
                      type="text"
                      className="checkout-input"
                      placeholder="10001"
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Credit Card Box */}
            {selectedPayment === "creditCard" && (
              <div className="checkout-card mt-5">
                <div
                  className="checkout-card-header text-white rounded-top-8 d-flex align-items-center gap-2"
                  style={{
                    background: "#3446b8",
                    padding: "25px 12px",
                  }}
                >
                  <i className="ph ph-credit-card text-xl"></i>
                  <h5 className="mb-0 fw-semibold">Credit Card Details</h5>
                </div>
                <div className="checkout-card-body bg-white shadow-sm p-4 rounded-bottom-8">
                  <div className="row g-3">
                    <div className="col-md-12">
                      <label className="checkout-label fw-medium mb-5">
                        Card Number *
                      </label>
                      <input
                        name="cardNumber"
                        type="text"
                        className="checkout-input"
                        placeholder="4242 4242 4242 4242"
                        maxLength="19"
                        value={formData.cardNumber}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, ""); // remove non-digits
                          value = value.replace(/(.{4})/g, "$1 ").trim(); // space every 4 digits
                          setFormData((prev) => ({
                            ...prev,
                            cardNumber: value,
                          }));
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="checkout-label fw-medium mb-5">
                        Expiry Date *
                      </label>
                      <input
                        name="expiry"
                        type="text"
                        className="checkout-input"
                        placeholder="MM/YY"
                        maxLength="5"
                        value={formData.expiry}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, ""); // only numbers
                          if (value.length > 2) {
                            value = value.slice(0, 2) + "/" + value.slice(2, 4); // insert slash
                          }
                          setFormData((prev) => ({ ...prev, expiry: value }));
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="checkout-label fw-medium mb-5">
                        CVV *
                      </label>
                      <input
                        name="cvv"
                        type="text"
                        className="checkout-input"
                        placeholder="123"
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="checkout-label fw-medium mb-5">
                        Name on Card *
                      </label>
                      <input
                        name="cardName"
                        type="text"
                        className="checkout-input"
                        placeholder="Enter Your Name"
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDE */}
          <div className="col-lg-4">
            <div className="checkout-summary-card bg-white shadow-sm rounded-8">
              <div className="checkout-summary-header bg-dark text-white px-4 py-3 rounded-top-8">
                <h5 className="mb-0 fw-semibold my-css">Order Summary</h5>
              </div>
              <div className="checkout-summary-body">
                {cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="checkout-item d-flex justify-content-between mb-20"
                  >
                    <div>
                      <h6 className="mb-1 text-gray-900">{item.name}</h6>
                      <small className="text-muted">
                        Qty: {item.quantity || 1}
                      </small>
                    </div>
                    <span className="fw-semibold text-gray-900">
                      ${(item.price * (item.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                ))}

                <hr className="my-3" />
                <div className="d-flex justify-content-between mb-2 mt-32">
                  <span className="text-gray-800">Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2 mt-18">
                  <span className="text-gray-800">Shipping ({zone})</span>
                  <span>${shipping.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-3 mt-18">
                  <span className="text-gray-800">Tax (10%)</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <hr />

                {/* Payment Options */}
                <div className="mt-20 mb-20">
                  <div className="checkout-payment-options">
                    <label className="checkout-payment-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="creditCard"
                        checked={selectedPayment === "creditCard"}
                        onChange={handlePaymentChange}
                      />
                      <div className="checkout-payment-content">
                        <i className="ph ph-credit-card text-primary text-lg me-3"></i>
                        <div className="pay-credit">
                          <h6 className="fw-semibold mb-1">
                            Pay with Credit Card
                          </h6>
                          <small className="text-gray-600">
                            Secure online payment with instant confirmation
                          </small>
                        </div>
                      </div>
                    </label>

                    <label className="checkout-payment-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cod"
                        checked={selectedPayment === "cod"}
                        onChange={handlePaymentChange}
                      />
                      <div className="checkout-payment-content">
                        <i className="ph ph-hand-coins text-warning text-lg me-3"></i>
                        <div className="pay-credit">
                          <h6 className="fw-semibold mb-1">Cash on Delivery</h6>
                          <small className="text-gray-600">
                            Pay in cash when your order arrives at your doorstep
                          </small>
                        </div>
                      </div>
                    </label>

                    <label className="checkout-payment-option">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="codcc"
                        checked={selectedPayment === "codcc"}
                        onChange={handlePaymentChange}
                      />
                      <div className="checkout-payment-content">
                        <i className="ph ph-identification-badge text-success text-lg me-3"></i>
                        <div className="pay-credit">
                          <h6 className="fw-semibold mb-1">
                            Card Payment on Delivery
                          </h6>
                          <small className="text-gray-600">
                            Pay with card when our delivery agent arrives
                          </small>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="fw-semibold text-gray-900">Total</h6>
                  <h5 className="fw-bold text-dark">${total.toFixed(2)}</h5>
                </div>
                <button
                  className="btn w-100 py-3 text-white fw-semibold checkout-btn-gradient rounded-8"
                  onClick={handlePlaceOrder}
                >
                  <i className="ph ph-lock text-lg me-2"></i> Place Order - $
                  {total.toFixed(2)}
                </button>
                <div className="text-center mt-3 d-flex align-items-center justify-content-center gap-2 mt-32">
                  <i className="ph ph-shield-check text-success"></i>
                  <small className="text-gray-600">
                    Protected by 256-bit SSL encryption
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;
